/*
 * Orizzonte Shop — ordinamento comparatore
 * Modulo indipendente dal motore nativo compare-modal.js
 */
(function () {
  'use strict';

  const MODULE_FLAG = 'compareSortReady';

  function getProductId(column) {
    const productClass = Array.from(column.classList).find((className) =>
      /^compare-col--\d+$/.test(className)
    );

    return productClass
      ? productClass.replace('compare-col--', '')
      : '';
  }

  function normalizedText(element) {
    return (element && element.textContent ? element.textContent : '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isEmptyValue(value) {
    return (
      !value ||
      /^(n\/?a|n\.d\.|non disponibile|-)$/i.test(value.trim())
    );
  }

  function parseComparableValue(text) {
    const normalized = String(text || '').trim();
    const upper = normalized.toUpperCase();

    /*
     * Ordinamento specifico delle classi energetiche.
     */
    if (/^[A-G](?:\+{1,3})?$/.test(upper)) {
      const energyOrder = {
        'A+++': -3,
        'A++': -2,
        'A+': -1,
        A: 0,
        B: 1,
        C: 2,
        D: 3,
        E: 4,
        F: 5,
        G: 6
      };

      return {
        type: 'number',
        value: energyOrder[upper]
      };
    }

    /*
     * Estrazione di valori numerici con virgola, punto e unità di misura.
     */
    const match = normalized.match(/-?[\d.,]+/);

    if (match) {
      let numericValue = match[0];

      if (numericValue.includes(',') && numericValue.includes('.')) {
        numericValue = numericValue
          .replace(/\./g, '')
          .replace(',', '.');
      } else if (numericValue.includes(',')) {
        numericValue = numericValue.replace(',', '.');
      } else if (/^-?\d{1,3}(?:\.\d{3})+$/.test(numericValue)) {
        numericValue = numericValue.replace(/\./g, '');
      }

      const parsedNumber = Number(numericValue);

      if (!Number.isNaN(parsedNumber)) {
        return {
          type: 'number',
          value: parsedNumber
        };
      }
    }

    return {
      type: 'text',
      value: normalized.toLocaleLowerCase('it')
    };
  }

  function compareValues(left, right, direction) {
    const leftEmpty = isEmptyValue(left.raw);
    const rightEmpty = isEmptyValue(right.raw);

    /*
     * I valori vuoti rimangono sempre in fondo,
     * sia nell’ordinamento crescente sia nel decrescente.
     */
    if (leftEmpty !== rightEmpty) {
      return leftEmpty ? 1 : -1;
    }

    if (leftEmpty && rightEmpty) {
      return left.originalIndex - right.originalIndex;
    }

    let result = 0;

    if (
      left.parsed.type === 'number' &&
      right.parsed.type === 'number'
    ) {
      result = left.parsed.value - right.parsed.value;
    } else {
      result = String(left.parsed.value).localeCompare(
        String(right.parsed.value),
        'it',
        {
          numeric: true,
          sensitivity: 'base'
        }
      );
    }

    /*
     * Mantiene stabile l’ordine quando due prodotti
     * hanno lo stesso valore.
     */
    if (result === 0) {
      return left.originalIndex - right.originalIndex;
    }

    return direction === 'desc' ? -result : result;
  }

  function initializeComparator(comparator) {
    if (
      !comparator ||
      comparator.dataset[MODULE_FLAG] === 'true'
    ) {
      return;
    }

    const compareArea = comparator.querySelector('.js-compare-area');
    const sortSelect = comparator.querySelector('.js-compare-sort');
    const differencesCheckbox = comparator.querySelector(
      '.js-compare-only-differences'
    );

    if (!compareArea || !sortSelect) {
      return;
    }

    comparator.dataset[MODULE_FLAG] = 'true';

    let originalOrder = [];
    let activeProductsSignature = '';
    let refreshTimer = null;
    let internalUpdate = false;

    function getRows() {
      return Array.from(
        compareArea.querySelectorAll('.compare-row')
      );
    }

    function getMetafieldRows() {
      return Array.from(
        compareArea.querySelectorAll('.compare-row--metafield')
      );
    }

    function getColumns(row) {
      return Array.from(row.children).filter((child) =>
        child.classList.contains('compare-col')
      );
    }

    function rememberOriginalOrder() {
      const firstProductRow = getRows().find(
        (row) => getColumns(row).length > 0
      );

      originalOrder = firstProductRow
        ? getColumns(firstProductRow)
            .map(getProductId)
            .filter(Boolean)
        : [];
    }

    function reorderAllRows(productOrder) {
      if (!productOrder.length) {
        return;
      }

      internalUpdate = true;

      getRows().forEach((row) => {
        const columns = getColumns(row);

        if (!columns.length) {
          return;
        }

        const columnsByProduct = new Map();

        columns.forEach((column) => {
          const productId = getProductId(column);

          if (productId) {
            columnsByProduct.set(productId, column);
          }
        });

        productOrder.forEach((productId) => {
          const column = columnsByProduct.get(productId);

          if (column) {
            row.appendChild(column);
          }
        });
      });

      window.requestAnimationFrame(() => {
        internalUpdate = false;
      });
    }

    function getRowLabel(row) {
      const labelElement = row.querySelector('.compare-label');

      if (labelElement) {
        return normalizedText(labelElement);
      }

      const firstColumn = getColumns(row)[0];

      return firstColumn
        ? normalizedText(firstColumn.querySelector('.compare-label'))
        : '';
    }

    function getColumnValue(column) {
      const valueElement = column.querySelector('.compare-value');

      return valueElement
        ? normalizedText(valueElement)
        : normalizedText(column);
    }

    function buildSortOptions() {
      const metafieldRows = getMetafieldRows();

      if (!metafieldRows.length) {
        return;
      }

      const previousValue = sortSelect.value;

      sortSelect.replaceChildren();

      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = 'Ordine di selezione';
      sortSelect.appendChild(defaultOption);

      metafieldRows.forEach((row, rowIndex) => {
        const label = getRowLabel(row);
        const columns = getColumns(row);

        const availableValues = columns
          .map(getColumnValue)
          .filter((value) => !isEmptyValue(value));

        if (!label || !availableValues.length) {
          return;
        }

        const ascendingOption = document.createElement('option');
        ascendingOption.value = `${rowIndex}:asc`;
        ascendingOption.textContent = `${label} — crescente`;

        const descendingOption = document.createElement('option');
        descendingOption.value = `${rowIndex}:desc`;
        descendingOption.textContent = `${label} — decrescente`;

        sortSelect.appendChild(ascendingOption);
        sortSelect.appendChild(descendingOption);
      });

      const previousOptionStillExists = Array.from(
        sortSelect.options
      ).some((option) => option.value === previousValue);

      if (previousOptionStillExists) {
        sortSelect.value = previousValue;
      }
    }

    function applyDifferencesFilter() {
      getMetafieldRows().forEach((row) => {
        if (
          !differencesCheckbox ||
          !differencesCheckbox.checked
        ) {
          row.hidden = false;
          return;
        }

        const values = getColumns(row).map((column) =>
          getColumnValue(column).toLocaleLowerCase('it')
        );

        row.hidden =
          values.length > 0 &&
          new Set(values).size === 1;
      });
    }

    function applySort() {
      if (!sortSelect.value) {
        reorderAllRows(originalOrder);
        return;
      }

      const selectedValue = sortSelect.value.split(':');
      const rowIndex = Number(selectedValue[0]);
      const direction = selectedValue[1];

      const selectedRow = getMetafieldRows()[rowIndex];

      if (!selectedRow) {
        return;
      }

      const rankedProducts = getColumns(selectedRow).map(
        (column, originalIndex) => {
          const rawValue = getColumnValue(column);

          return {
            productId: getProductId(column),
            originalIndex,
            raw: rawValue,
            parsed: parseComparableValue(rawValue)
          };
        }
      );

      rankedProducts.sort((left, right) =>
        compareValues(left, right, direction)
      );

      reorderAllRows(
        rankedProducts
          .map((item) => item.productId)
          .filter(Boolean)
      );
    }

    function applyTools() {
      applyDifferencesFilter();
      applySort();
    }

    function refresh() {
      const rows = getRows();

      if (!rows.length) {
        return;
      }

      /*
       * Enterprise inserisce nell’area del confronto solamente
       * le righe interne, eliminando il contenitore compare-temp.
       */
      const firstProductRow = rows.find(
        (row) => getColumns(row).length > 0
      );

      if (!firstProductRow) {
        return;
      }

      const productsSignature = getColumns(firstProductRow)
        .map(getProductId)
        .filter(Boolean)
        .join('|');

      if (!productsSignature) {
        return;
      }

      if (productsSignature !== activeProductsSignature) {
        activeProductsSignature = productsSignature;
        rememberOriginalOrder();
      }

      buildSortOptions();
      applyTools();
    }

    function scheduleRefresh() {
      if (internalUpdate) {
        return;
      }

      window.clearTimeout(refreshTimer);

      refreshTimer = window.setTimeout(refresh, 80);
    }

    sortSelect.addEventListener('change', applySort);

    if (differencesCheckbox) {
      differencesCheckbox.addEventListener(
        'change',
        applyDifferencesFilter
      );
    }

    const observer = new MutationObserver(scheduleRefresh);

    observer.observe(compareArea, {
      childList: true,
      subtree: true
    });

    scheduleRefresh();
  }

  function boot() {
    document
      .querySelectorAll('compare-modal')
      .forEach(initializeComparator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {
      once: true
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();